import React from "react";
import styled from "styled-components";

const Wrapper = styled.div`
  padding: 0rem 2rem 4rem;
  max-width: 900px;
  margin: auto;
  background: #fff;
  color: #1f2937;
  line-height: 1.6;
  margin-top: 90px;
  h1,
  h2 {
    color: #111827;
    margin-top: 2rem;
  }

  ul {
    margin-left: 1.5rem;
  }

  a {
    color: #3b82f6;
    text-decoration: none;
  }
`;

const PrivacyPolicy: React.FC = () => (
  <Wrapper>
    <h1>Privacy Policy</h1>
    <p>
      <strong>Last updated:</strong> October 28, 2026
    </p>

    <p>
      This Privacy Policy explains how VendorOrbis ("we", "us", "our")
      collects, uses, and shares information when you use our web or mobile
      applications and related services (the "Services"). If you do not agree
      with this policy, do not use the Services.
    </p>

    <h2>1. Information We Collect</h2>
    <h3>Information you provide</h3>
    <ul>
      <li>Account data such as name, email, and login credentials</li>
      <li>Business data such as events, inventory, pricing, and sales entries</li>
      <li>Payment method labels and QR payment codes you upload</li>
      <li>Support requests and communications</li>
      <li>Preferences such as notification settings</li>
    </ul>

    <h3>Information collected automatically</h3>
    <ul>
      <li>Device and app data (IP, OS, device model, app version)</li>
      <li>Usage and diagnostic data (crash logs, performance metrics)</li>
      <li>Session data and feature usage analytics</li>
      <li>Whether you open our emails and which links in them you click</li>
      <li>
        If you reach our website from an ad, the ad campaign details in the
        link (such as which campaign and ad you clicked)
      </li>
    </ul>

    <h3>Permissions for Square card payments</h3>
    <p>
      If you enable Square card payments, the mobile app may request device
      permissions needed to connect readers and support Tap to Pay. These
      permissions are used only to enable Square payment features:
    </p>
    <ul>
      <li>
        <strong>Location</strong> – required by Square to discover and connect
        nearby readers.
      </li>
      <li>
        <strong>Bluetooth / Nearby Devices</strong> – required to connect and
        communicate with Square readers.
      </li>
      <li>
        <strong>Microphone (Android)</strong> – required for some reader
        hardware types.
      </li>
      <li>
        <strong>Phone State (Android)</strong> – required by Square to identify
        the device for reader pairing.
      </li>
    </ul>
    <p>
      We do not use these permissions for tracking or advertising. They are
      used only to support Square payment functionality.
    </p>

    <h3>Biometric authentication</h3>
    <p>
      If you enable device biometrics (Face ID or fingerprint), authentication
      is handled by your device. We do not receive or store your biometric data.
    </p>

    <h2>2. How We Use Information</h2>
    <ul>
      <li>Provide and operate the Services</li>
      <li>Maintain accounts, authentication, and security</li>
      <li>Generate AI insights and recommendations from your business data</li>
      <li>Deliver notifications, reminders, and updates</li>
      <li>
        Send welcome, getting-started, and tips emails (you can unsubscribe at
        any time)
      </li>
      <li>Improve performance, reliability, and user experience</li>
      <li>Comply with legal obligations</li>
    </ul>

    <h2>3. No Payment Processing</h2>
    <p>
      VendorOrbis does not process, transmit, or settle payments. We do not
      collect or store your customers' payment card information. Any payment
      method labels or QR payment codes you add are stored for display and
      record-keeping only. (This is about payments you take from your own
      customers. Payments for your VendorOrbis plan are covered in Section 5.)
    </p>
    <p>
      If you enable Square card payments, Square (Block, Inc.) processes card
      transactions. Your use of Square is governed by Square’s terms and
      privacy policy:
    </p>
    <ul>
      <li>
        <a href="https://squareup.com/legal" target="_blank" rel="noreferrer">
          Square Terms of Service
        </a>
      </li>
      <li>
        <a
          href="https://squareup.com/us/en/legal/general/privacy"
          target="_blank"
          rel="noreferrer"
        >
          Square Privacy Policy
        </a>
      </li>
    </ul>
    <p>
      If you enable Square payments, Square (Block, Inc.) processes card
      transactions. Your use of Square is governed by Square’s terms and
      privacy policy:
    </p>
    <ul>
      <li>
        <a href="https://squareup.com/legal" target="_blank" rel="noreferrer">
          Square Terms of Service
        </a>
      </li>
      <li>
        <a
          href="https://squareup.com/us/en/legal/general/privacy"
          target="_blank"
          rel="noreferrer"
        >
          Square Privacy Policy
        </a>
      </li>
    </ul>

    <h2>4. AI Processing</h2>
    <p>
      We may use third-party AI services to generate insights and
      recommendations. We only send the data needed to generate those results.
      AI outputs are informational and should be reviewed before use.
    </p>

    <h2>5. Subscriptions</h2>
    <p>
      You can buy a paid plan in the iPhone or Android app, through the Apple
      App Store or Google Play, or on our website. Purchases on our website are
      handled by RevenueCat, which manages subscriptions, and Stripe, which
      processes card payments. Your card details go directly to Stripe; we
      never see or store full card numbers.
    </p>
    <p>
      To provide and support your plan, we and these providers receive
      subscription details such as your account ID, email address, plan,
      price, billing country or postal code, payment status, and trial and
      renewal dates. Their handling of your information is described in their
      privacy policies:
    </p>
    <ul>
      <li>
        <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noreferrer">
          RevenueCat Privacy Policy
        </a>
      </li>
      <li>
        <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer">
          Stripe Privacy Policy
        </a>
      </li>
    </ul>

    <h2>6. Sharing of Information</h2>
    <ul>
      <li>Service providers that host or process data on our behalf</li>
      <li>
        Subscription and payment providers (Apple, Google, RevenueCat, and
        Stripe) for billing and verification
      </li>
      <li>AI service providers for insights generation</li>
      <li>Meta (Facebook and Instagram), to measure our ads on our website</li>
      <li>Square, if you enable card payments through Square</li>
      <li>Legal or regulatory requests when required by law</li>
      <li>Business transfers in connection with a merger or acquisition</li>
    </ul>
    <p>We do not sell your personal information.</p>

    <h2>7. Cookies and Local Storage</h2>
    <p>
      Our web app uses cookies and local storage to keep you signed in, remember
      preferences, and improve performance. You can control cookies through
      your browser settings, but some features may not function properly.
    </p>
    <h3>Advertising</h3>
    <p>
      Our website uses the Meta Pixel, a tool from Meta Platforms, Inc. (which
      runs Facebook and Instagram), to measure how well our ads work. It uses
      cookies to record visits to our website and actions such as signing
      up, starting a free trial, or subscribing, and Meta may link this to
      your Facebook or Instagram account. If you sign up or subscribe on our
      website, our servers also tell Meta about it directly, using Meta&apos;s
      Conversions API. We send a scrambled (hashed) copy of your email address
      and account ID, your IP address and browser type, Meta&apos;s cookie IDs,
      and the plan and price. Meta uses this to match the event to its own
      records. We do not send this for accounts created in our mobile apps or
      for purchases made through the App Store or Google Play. We use it only
      to measure and improve our ads. You can control this in your{" "}
      <a href="https://www.facebook.com/adpreferences" target="_blank" rel="noreferrer">
        Meta ad preferences
      </a>
      , and by blocking cookies in your browser. See the{" "}
      <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noreferrer">
        Meta Privacy Policy
      </a>
      .
    </p>
    <h3>Emails we send</h3>
    <p>
      Our welcome, getting-started, and tips emails include a small image and
      links that tell us whether an email was opened and which links were
      clicked. We use this only to learn which emails are helpful, and we do
      not share it. Your email app may block the image, and you can stop these
      emails at any time with the unsubscribe link at the bottom of each one.
      Account emails, such as verification codes and password resets, are
      still sent.
    </p>
    <p>
      If you subscribe on our website, RevenueCat also sends receipts and
      billing emails on our behalf, such as trial reminders and failed-payment
      notices. These are part of your subscription and are sent while it is
      active.
    </p>

    <h2>8. Data Retention</h2>
    <p>
      We retain data for as long as your account is active or as needed to
      provide the Services. You may request deletion by contacting
      <a href="mailto:support@vendororbis.com"> support@vendororbis.com</a>.
      We may retain limited data as required by law or for legitimate business
      purposes.
    </p>

    <h2>9. Security</h2>
    <p>
      We use reasonable technical and organizational measures to protect your
      data, including encryption in transit. No system can be guaranteed 100%
      secure.
    </p>

    <h2>10. Your Rights</h2>
    <p>
      Depending on your location, you may have rights to access, correct,
      delete, or restrict the use of your personal information. Contact us at
      <a href="mailto:support@vendororbis.com"> support@vendororbis.com</a> to
      make a request.
    </p>

    <h2>11. Children</h2>
    <p>
      The Services are not intended for individuals under 18. We do not
      knowingly collect personal information from children.
    </p>

    <h2>12. Changes to this Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. We will post updates
      in the app and may notify you by email or in-app notice.
    </p>

    <h2>13. Contact Us</h2>
    <p>
      If you have questions or requests, contact
      <a href="mailto:support@vendororbis.com"> support@vendororbis.com</a>.
    </p>

    <p>&copy; {new Date().getFullYear()} VendorOrbis. All rights reserved.</p>
  </Wrapper>
);

export default PrivacyPolicy;
